import { copyLocalFiles } from '../lib/localMedia';
import { useLists } from './lists';
import { activeProfile, GUEST, hasStored, profileFor, setActiveProfile, storageKey } from './profile';
import { defaultSettings, restoreLocalMedia, useSettings } from './settings';

const SETTINGS = 'kcl-canvas-settings';
const LISTS = 'kcl-canvas-lists';

/**
 * Points the settings and lists stores at the given account's own local copy, so one
 * account's settings are never shown on, or uploaded to, another.
 *
 * - An account seen before on this device gets back exactly what it had.
 * - A new account starts from defaults (its server copy, if any, then arrives via sync) –
 *   except straight after signing in from signed-out use, when the guest's setup is
 *   carried over so first-time sign-up doesn't lose it.
 * - Signing out returns to the guest profile.
 */
export function switchProfile(userId: string | null | undefined): void {
  const from = activeProfile();
  const to = profileFor(userId);
  if (from === to) return;

  const adoptGuest = from === GUEST && to !== GUEST && !hasStored(SETTINGS, to) && !hasStored(LISTS, to);
  const localIds = useSettings
    .getState()
    .media.items.filter((i) => i.local)
    .map((i) => i.id);

  setActiveProfile(to);
  useSettings.persist.setOptions({ name: storageKey(SETTINGS, to) });
  useLists.persist.setOptions({ name: storageKey(LISTS, to) });

  if (adoptGuest) {
    // Re-save the current state under the new account's keys.
    useSettings.setState((s) => ({ ...s }));
    useLists.setState((s) => ({ ...s }));
    if (localIds.length) void copyLocalFiles(from, to, localIds).catch(() => undefined);
    return;
  }

  if (hasStored(SETTINGS, to)) void useSettings.persist.rehydrate();
  else useSettings.setState({ ...defaultSettings });
  if (hasStored(LISTS, to)) void useLists.persist.rehydrate();
  else useLists.getState().replaceAll([]);
  void restoreLocalMedia(true);
}
